import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { challengesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, ArrowLeft, TrendingUp, TrendingDown, BarChart3, 
  Calendar, Clock, Target, Award, Activity, Wallet, PieChart,
  ArrowUpRight, ArrowDownRight, Minus, Filter, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Performance Stat Card
const StatCard = ({ title, value, subValue, icon: Icon, trend, color = 'blue' }) => {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/10',
    green: 'text-green-400 bg-green-500/10',
    red: 'text-red-400 bg-red-500/10',
    cyan: 'text-cyan-400 bg-cyan-500/10',
    yellow: 'text-yellow-400 bg-yellow-500/10',
  };

  return (
    <Card className="bg-[hsl(220,20%,7%)] border-white/10">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">{title}</p>
            <p className="text-2xl font-bold text-white font-mono">{value}</p>
            {subValue && (
              <p className={`text-sm mt-1 flex items-center gap-1 ${
                trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'
              }`}>
                {trend === 'up' && <ArrowUpRight className="w-3 h-3" />}
                {trend === 'down' && <ArrowDownRight className="w-3 h-3" />}
                {trend === 'neutral' && <Minus className="w-3 h-3" />}
                {subValue}
              </p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Simple Bar Chart Component
const SimpleBarChart = ({ data, title }) => {
  const maxValue = Math.max(...data.map(d => Math.abs(d.value)), 1);
  
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-400">{title}</h4>
      <div className="space-y-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-3">
            <span className="text-xs text-gray-500 w-16">{item.label}</span>
            <div className="flex-1 h-6 bg-white/5 rounded overflow-hidden">
              <div 
                className={`h-full rounded transition-all ${
                  item.value >= 0 ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-red-500 to-rose-500'
                }`}
                style={{ width: `${(Math.abs(item.value) / maxValue) * 100}%` }}
              />
            </div>
            <span className={`text-xs font-mono w-16 text-right ${item.value >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {item.value >= 0 ? '+' : ''}{formatCurrency(item.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Trade Row Component
const TradeRow = ({ trade }) => {
  const isProfit = trade.profit_loss >= 0;
  
  return (
    <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-xs ${trade.type === 'BUY' ? 'text-green-400 border-green-500/30' : 'text-red-400 border-red-500/30'}`}>
            {trade.type}
          </Badge>
          <span className="font-mono text-white">{trade.symbol}</span>
        </div>
      </td>
      <td className="py-3 px-4 text-gray-400 text-sm">{formatDate(trade.open_time)}</td>
      <td className="py-3 px-4 font-mono text-white">{trade.lot_size}</td>
      <td className="py-3 px-4 font-mono text-gray-400">{trade.open_price?.toFixed(5)}</td>
      <td className="py-3 px-4 font-mono text-gray-400">{trade.close_price?.toFixed(5)}</td>
      <td className="py-3 px-4">
        <span className={`font-mono font-medium ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
          {isProfit ? '+' : ''}{formatCurrency(trade.profit_loss)}
        </span>
      </td>
      <td className="py-3 px-4 text-gray-400 text-sm">{trade.duration || '-'}</td>
    </tr>
  );
};

const TradingHistory = () => {
  const [challenges, setChallenges] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedAccount && selectedAccount !== 'all') {
      fetchTrades(selectedAccount);
    } else {
      // Aggregate all trades
      const allTrades = challenges.flatMap(c => c.trades || []);
      setTrades(allTrades.sort((a, b) => new Date(b.open_time) - new Date(a.open_time)));
    }
  }, [selectedAccount, challenges]);

  const fetchData = async () => {
    try {
      const res = await challengesAPI.getMyChallenges();
      setChallenges(res.data);
      
      // Aggregate all trades from all accounts
      const allTrades = res.data.flatMap(c => c.trades || []);
      setTrades(allTrades.sort((a, b) => new Date(b.open_time) - new Date(a.open_time)));
    } catch (error) {
      toast.error('Failed to load trading data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrades = async (accountId) => {
    try {
      const account = challenges.find(c => c.id === accountId);
      if (account) {
        setTrades((account.trades || []).sort((a, b) => new Date(b.open_time) - new Date(a.open_time)));
      }
    } catch (error) {
      toast.error('Failed to load trades');
    }
  };

  // Calculate performance metrics
  const calculateMetrics = () => {
    const accountsToAnalyze = selectedAccount === 'all' 
      ? challenges 
      : challenges.filter(c => c.id === selectedAccount);

    const totalTrades = trades.length;
    const winningTrades = trades.filter(t => t.profit_loss > 0).length;
    const losingTrades = trades.filter(t => t.profit_loss < 0).length;
    const winRate = totalTrades > 0 ? ((winningTrades / totalTrades) * 100).toFixed(1) : '0.0';
    
    const totalProfit = trades.filter(t => t.profit_loss > 0).reduce((sum, t) => sum + t.profit_loss, 0);
    const totalLoss = Math.abs(trades.filter(t => t.profit_loss < 0).reduce((sum, t) => sum + t.profit_loss, 0));
    const netPnL = totalProfit - totalLoss;
    const profitFactor = totalLoss > 0 ? (totalProfit / totalLoss).toFixed(2) : totalProfit > 0 ? '∞' : '0.00';
    
    const avgWin = winningTrades > 0 ? totalProfit / winningTrades : 0;
    const avgLoss = losingTrades > 0 ? totalLoss / losingTrades : 0;
    const riskRewardRatio = avgLoss > 0 ? (avgWin / avgLoss).toFixed(2) : avgWin > 0 ? '∞' : '0.00';

    // Calculate by symbol
    const symbolStats = {};
    trades.forEach(t => {
      if (!symbolStats[t.symbol]) {
        symbolStats[t.symbol] = { wins: 0, losses: 0, pnl: 0 };
      }
      if (t.profit_loss > 0) symbolStats[t.symbol].wins++;
      else symbolStats[t.symbol].losses++;
      symbolStats[t.symbol].pnl += t.profit_loss;
    });

    const topSymbols = Object.entries(symbolStats)
      .map(([symbol, stats]) => ({ symbol, ...stats }))
      .sort((a, b) => b.pnl - a.pnl)
      .slice(0, 5);

    // Weekly P&L (last 7 entries)
    const weeklyPnL = [];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayTrades = trades.filter(t => {
        const tradeDate = new Date(t.open_time);
        return tradeDate.toDateString() === date.toDateString();
      });
      const dayPnL = dayTrades.reduce((sum, t) => sum + (t.profit_loss || 0), 0);
      weeklyPnL.push({ label: days[date.getDay()], value: dayPnL });
    }

    return {
      totalTrades,
      winningTrades,
      losingTrades,
      winRate,
      netPnL,
      totalProfit,
      totalLoss,
      profitFactor,
      avgWin,
      avgLoss,
      riskRewardRatio,
      topSymbols,
      weeklyPnL,
      totalAccounts: accountsToAnalyze.length,
      fundedAccounts: accountsToAnalyze.filter(c => c.status === 'funded').length
    };
  };

  const metrics = calculateMetrics();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="trading-history-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
        >
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="border-white/10 text-white hover:bg-white/5"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold text-white">Performance Analytics</h1>
              <p className="text-gray-400 text-sm">Track your trading performance and analyze your strategies</p>
            </div>
          </div>
          
          {/* Account Filter */}
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="w-48 bg-[hsl(220,20%,7%)] border-white/10 text-white">
                <SelectValue placeholder="Select Account" />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(220,20%,7%)] border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/10">All Accounts</SelectItem>
                {challenges.map(c => (
                  <SelectItem key={c.id} value={c.id} className="text-white hover:bg-white/10">
                    {c.package_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[hsl(220,20%,7%)] border border-white/10">
            <TabsTrigger value="overview" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="trades" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              <Activity className="w-4 h-4 mr-2" />
              Trade Journal
            </TabsTrigger>
            <TabsTrigger value="analysis" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              <PieChart className="w-4 h-4 mr-2" />
              Analysis
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-4"
            >
              <StatCard 
                title="Net P&L" 
                value={formatCurrency(metrics.netPnL)} 
                subValue={metrics.netPnL >= 0 ? 'Profitable' : 'Loss'}
                trend={metrics.netPnL >= 0 ? 'up' : 'down'}
                icon={Wallet} 
                color={metrics.netPnL >= 0 ? 'green' : 'red'} 
              />
              <StatCard 
                title="Win Rate" 
                value={`${metrics.winRate}%`} 
                subValue={`${metrics.winningTrades}W / ${metrics.losingTrades}L`}
                trend={parseFloat(metrics.winRate) >= 50 ? 'up' : 'down'}
                icon={Target} 
                color={parseFloat(metrics.winRate) >= 50 ? 'green' : 'yellow'} 
              />
              <StatCard 
                title="Profit Factor" 
                value={metrics.profitFactor} 
                subValue="Risk-adjusted return"
                trend={parseFloat(metrics.profitFactor) >= 1.5 ? 'up' : 'neutral'}
                icon={TrendingUp} 
                color="cyan" 
              />
              <StatCard 
                title="Total Trades" 
                value={metrics.totalTrades} 
                subValue={`${metrics.totalAccounts} account(s)`}
                trend="neutral"
                icon={Activity} 
                color="blue" 
              />
            </motion.div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Weekly P&L Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-cyan-400" />
                      Weekly Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SimpleBarChart data={metrics.weeklyPnL} title="Daily P&L (Last 7 Days)" />
                  </CardContent>
                </Card>
              </motion.div>

              {/* Top Instruments */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-cyan-400" />
                      Top Instruments
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {metrics.topSymbols.length > 0 ? (
                      <div className="space-y-3">
                        {metrics.topSymbols.map((item, index) => (
                          <div key={item.symbol} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded bg-gradient-to-r from-blue-500/20 to-cyan-500/20 flex items-center justify-center text-xs font-bold text-cyan-400">
                                {index + 1}
                              </span>
                              <span className="font-mono font-medium text-white">{item.symbol}</span>
                            </div>
                            <div className="text-right">
                              <p className={`font-mono font-medium ${item.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {item.pnl >= 0 ? '+' : ''}{formatCurrency(item.pnl)}
                              </p>
                              <p className="text-xs text-gray-500">{item.wins}W / {item.losses}L</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No trades yet</p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Additional Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-gray-400 text-sm">Avg. Win</p>
                  <p className="text-xl font-mono font-bold text-green-400">{formatCurrency(metrics.avgWin)}</p>
                </CardContent>
              </Card>
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-gray-400 text-sm">Avg. Loss</p>
                  <p className="text-xl font-mono font-bold text-red-400">-{formatCurrency(metrics.avgLoss)}</p>
                </CardContent>
              </Card>
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-gray-400 text-sm">Risk/Reward</p>
                  <p className="text-xl font-mono font-bold text-cyan-400">{metrics.riskRewardRatio}</p>
                </CardContent>
              </Card>
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-gray-400 text-sm">Best Trade</p>
                  <p className="text-xl font-mono font-bold text-green-400">
                    {trades.length > 0 ? formatCurrency(Math.max(...trades.map(t => t.profit_loss || 0))) : '$0.00'}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Trade Journal Tab */}
          <TabsContent value="trades" className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white">Trade Journal</CardTitle>
                  <Button variant="outline" size="sm" className="border-white/10 text-gray-400 hover:text-white">
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </CardHeader>
                <CardContent>
                  {trades.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Symbol</th>
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Date</th>
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Lot Size</th>
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Open</th>
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Close</th>
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">P&L</th>
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trades.slice(0, 20).map((trade, index) => (
                            <TradeRow key={index} trade={trade} />
                          ))}
                        </tbody>
                      </table>
                      {trades.length > 20 && (
                        <p className="text-center text-gray-500 py-4">
                          Showing 20 of {trades.length} trades
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Activity className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">No Trades Yet</h3>
                      <p className="text-gray-500 mb-4">Start trading to see your trade history here</p>
                      <Button onClick={() => navigate('/challenges')} className="bg-gradient-to-r from-blue-500 to-cyan-500">
                        Start Trading
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Analysis Tab */}
          <TabsContent value="analysis" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Win/Loss Distribution */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Win/Loss Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex-1">
                        <div className="h-4 rounded-full overflow-hidden bg-red-500/20 flex">
                          <div 
                            className="h-full bg-gradient-to-r from-green-500 to-emerald-500"
                            style={{ width: `${metrics.winRate}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                        <p className="text-green-400 text-sm">Winning Trades</p>
                        <p className="text-2xl font-bold text-white">{metrics.winningTrades}</p>
                        <p className="text-green-400 font-mono">+{formatCurrency(metrics.totalProfit)}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                        <p className="text-red-400 text-sm">Losing Trades</p>
                        <p className="text-2xl font-bold text-white">{metrics.losingTrades}</p>
                        <p className="text-red-400 font-mono">-{formatCurrency(metrics.totalLoss)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Trading Tips */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Award className="w-5 h-5 text-cyan-400" />
                      Performance Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {parseFloat(metrics.winRate) >= 50 ? (
                      <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                        <p className="text-green-400 text-sm font-medium">✓ Great win rate!</p>
                        <p className="text-gray-400 text-xs">Your {metrics.winRate}% win rate is above average.</p>
                      </div>
                    ) : (
                      <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                        <p className="text-yellow-400 text-sm font-medium">↑ Improve win rate</p>
                        <p className="text-gray-400 text-xs">Focus on quality setups to improve your {metrics.winRate}% win rate.</p>
                      </div>
                    )}
                    
                    {parseFloat(metrics.profitFactor) >= 1.5 ? (
                      <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                        <p className="text-green-400 text-sm font-medium">✓ Solid profit factor</p>
                        <p className="text-gray-400 text-xs">Your {metrics.profitFactor} profit factor shows good risk management.</p>
                      </div>
                    ) : (
                      <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                        <p className="text-yellow-400 text-sm font-medium">↑ Work on risk/reward</p>
                        <p className="text-gray-400 text-xs">Aim for larger winners and smaller losers.</p>
                      </div>
                    )}

                    {metrics.topSymbols.length > 0 && metrics.topSymbols[0].pnl > 0 && (
                      <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                        <p className="text-blue-400 text-sm font-medium">💡 Best performing pair</p>
                        <p className="text-gray-400 text-xs">{metrics.topSymbols[0].symbol} is your most profitable instrument.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TradingHistory;
